/**
 * JDVariantModal.js — Paste JD → AI tailors resume → Save as variant
 *
 * Three steps:
 *   1. "paste"     — user pastes a job description
 *   2. "loading"   — AI is generating the variant (spinner)
 *   3. "done"      — shows what changed + Save button
 */
import { useState } from "react";

function JDVariantModal({ isOpen, onClose, onVariantCreated, credits, authToken, theme }) {
    const [step, setStep]         = useState("paste");
    const [jdText, setJdText]     = useState("");
    const [error, setError]       = useState("");
    const [result, setResult]     = useState(null); // { variant, credits_remaining }

    if (!isOpen) return null;

    const getAuthHeaders = () =>
        authToken ? { Authorization: `Bearer ${authToken}` } : {};

    const handleGenerate = async () => {
        if (!jdText.trim() || jdText.trim().length < 50) {
            setError("Please paste the full job description (at least 50 characters).");
            return;
        }
        setError("");
        setStep("loading");

        try {
            const res = await fetch("/api/ats/variants", {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeaders() },
                body: JSON.stringify({ job_description: jdText }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to generate variant.");
            setResult(data);
            setStep("done");
        } catch (err) {
            setError(err.message);
            setStep("paste");
        }
    };

    const handleSave = () => {
        if (result?.variant) {
            onVariantCreated(result.variant, result.credits_remaining);
        }
        handleClose();
    };

    const handleClose = () => {
        setStep("paste");
        setJdText("");
        setError("");
        setResult(null);
        onClose();
    };

    const isDark = theme === 'dark';

    /* ── Shared overlay ── */
    const overlayStyle = {
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)',
        padding: 16,
    };
    const boxStyle = {
        background: isDark ? '#18181b' : '#ffffff',
        borderRadius: 16,
        border: isDark ? '1px solid #27272a' : '1px solid #e4e4e7',
        width: '100%',
        maxWidth: 540,
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: isDark ? '0 24px 64px rgba(0,0,0,0.6)' : '0 24px 64px rgba(0,0,0,0.1)',
        fontFamily: "'Inter','Segoe UI',sans-serif",
    };

    /* ═══ STEP 1: PASTE ═══ */
    if (step === "paste") return (
        <div style={overlayStyle} onClick={e => e.target === e.currentTarget && handleClose()}>
            <div style={boxStyle}>
                {/* Header */}
                <div style={{ padding: '20px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>✦ JD Variant Creator</div>
                        <div style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b', marginTop: 3 }}>
                            Paste a job description — AI will tailor your resume to match it
                        </div>
                    </div>
                    <button onClick={handleClose} style={{ background: 'none', border: 'none', color: isDark ? '#94a3b8' : '#475569', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
                </div>

                {/* Cost notice */}
                <div style={{ margin: '14px 24px 0', padding: '8px 12px', background: isDark ? '#27272a' : '#f4f4f5', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: isDark ? '#94a3b8' : '#4b5563' }}>
                    <span>⚡</span>
                    <span>Costs <strong style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>5 credits</strong> per variant — you have <strong style={{ color: credits >= 5 ? '#4ade80' : '#f87171' }}>{credits ?? '...'}</strong></span>
                </div>

                {/* JD textarea */}
                <div style={{ padding: '14px 24px' }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 8 }}>
                        Job Description
                    </label>
                    <textarea
                        autoFocus
                        value={jdText}
                        onChange={e => { setJdText(e.target.value); setError(""); }}
                        placeholder="Paste the full job description here…&#10;&#10;e.g. 'We are looking for an HR Executive with 2+ years of experience in payroll, compliance and HRMS tools...'"
                        rows={10}
                        style={{
                            width: '100%', background: isDark ? '#27272a' : '#ffffff', border: isDark ? '1px solid #3f3f46' : '1px solid #cbd5e1',
                            borderRadius: 8, color: isDark ? '#e2e8f0' : '#0f172a', fontSize: 12, lineHeight: 1.6,
                            padding: '10px 12px', resize: 'vertical', fontFamily: 'inherit',
                            outline: 'none', boxSizing: 'border-box',
                        }}
                        onFocus={e => (e.target.style.borderColor = '#0ea5e9')}
                        onBlur={e => (e.target.style.borderColor = isDark ? '#3f3f46' : '#cbd5e1')}
                    />
                    {jdText.trim().length > 0 && (
                        <div style={{ fontSize: 10, color: jdText.trim().length >= 50 ? '#4ade80' : '#f87171', marginTop: 4, textAlign: 'right' }}>
                            {jdText.trim().length} chars {jdText.trim().length < 50 ? '(need 50+)' : '✓'}
                        </div>
                    )}
                </div>

                {error && (
                    <div style={{ margin: '0 24px 12px', padding: '8px 12px', background: isDark ? '#ef444420' : '#fee2e2', border: isDark ? '1px solid #ef4444' : '1px solid #fca5a5', borderRadius: 8, fontSize: 12, color: isDark ? '#f87171' : '#b91c1c' }}>
                        {error}
                    </div>
                )}

                {/* Actions */}
                <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10 }}>
                    <button onClick={handleClose} style={{ flex: 1, padding: '10px', background: isDark ? '#27272a' : '#ffffff', border: isDark ? '1px solid #3f3f46' : '1px solid #cbd5e1', borderRadius: 8, color: isDark ? '#94a3b8' : '#4b5563', fontSize: 13, cursor: 'pointer' }}>
                        Cancel
                    </button>
                    <button
                        onClick={handleGenerate}
                        disabled={(credits ?? 0) < 5}
                        style={{
                            flex: 2, padding: '10px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                            cursor: (credits ?? 0) >= 5 ? 'pointer' : 'not-allowed',
                            background: (credits ?? 0) >= 5 ? 'linear-gradient(135deg, #0ea5e9, #2563eb)' : (isDark ? '#27272a' : '#f4f4f5'),
                            color: (credits ?? 0) >= 5 ? '#fff' : (isDark ? '#71717a' : '#a1a1aa'),
                            border: (credits ?? 0) >= 5 ? 'none' : (isDark ? 'none' : '1px solid #e4e4e7'),
                        }}
                    >
                        {(credits ?? 0) < 5 ? 'Not enough credits' : '✦ Generate JD Variant (5 credits)'}
                    </button>
                </div>
            </div>
        </div>
    );

    /* ═══ STEP 2: LOADING ═══ */
    if (step === "loading") return (
        <div style={overlayStyle}>
            <div style={{ ...boxStyle, padding: '48px 24px', textAlign: 'center' }}>
                {/* Spinner */}
                <div style={{
                    width: 52, height: 52, margin: '0 auto 20px',
                    borderRadius: '50%',
                    border: isDark ? '3px solid #27272a' : '3px solid #e4e4e7',
                    borderTop: '3px solid #0ea5e9',
                    animation: 'spin 0.8s linear infinite',
                }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <div style={{ fontSize: 16, fontWeight: 600, color: isDark ? '#f1f5f9' : '#0f172a', marginBottom: 8 }}>Tailoring your resume…</div>
                <div style={{ fontSize: 12, color: isDark ? '#64748b' : '#4b5563' }}>AI is analyzing the JD and personalizing your resume. This takes 5–15 seconds.</div>
            </div>
        </div>
    );

    /* ═══ STEP 3: DONE ═══ */
    if (step === "done" && result) return (
        <div style={overlayStyle} onClick={e => e.target === e.currentTarget && handleClose()}>
            <div style={boxStyle}>
                <div style={{ padding: '20px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: isDark ? '#4ade80' : '#16a34a' }}>✓ Variant Ready!</div>
                        <div style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b', marginTop: 3 }}>
                            "{result.variant?.name}"
                        </div>
                    </div>
                    <button onClick={handleClose} style={{ background: 'none', border: 'none', color: isDark ? '#94a3b8' : '#475569', fontSize: 20, cursor: 'pointer' }}>×</button>
                </div>

                <div style={{ padding: '16px 24px' }}>
                    {/* Info cards */}
                    {[
                        { icon: '📝', label: 'Summary Rewritten', desc: 'Tailored to match the JD\'s requirements and keywords' },
                        { icon: '🛠', label: 'Skills Updated', desc: 'Missing JD keywords added to your skills list' },
                        { icon: '⚡', label: 'Bullets Enhanced', desc: 'Experience points updated with JD-relevant vocabulary' },
                    ].map((item, i) => (
                        <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: i < 2 ? (isDark ? '1px solid #27272a' : '1px solid #e4e4e7') : 'none' }}>
                            <span style={{ fontSize: 18, lineHeight: 1 }}>{item.icon}</span>
                            <div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#e2e8f0' : '#0f172a' }}>{item.label}</div>
                                <div style={{ fontSize: 11, color: isDark ? '#64748b' : '#4b5563', marginTop: 2 }}>{item.desc}</div>
                            </div>
                        </div>
                    ))}

                    <div style={{ marginTop: 14, padding: '8px 12px', background: isDark ? '#27272a' : '#f4f4f5', borderRadius: 8, fontSize: 11, color: isDark ? '#94a3b8' : '#4b5563' }}>
                        ⚡ Credits remaining: <strong style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>{result.credits_remaining}</strong>
                    </div>
                </div>

                <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10 }}>
                    <button onClick={handleClose} style={{ flex: 1, padding: '10px', background: isDark ? '#27272a' : '#ffffff', border: isDark ? '1px solid #3f3f46' : '1px solid #cbd5e1', borderRadius: 8, color: isDark ? '#94a3b8' : '#4b5563', fontSize: 13, cursor: 'pointer' }}>
                        Discard
                    </button>
                    <button
                        onClick={handleSave}
                        style={{ flex: 2, padding: '10px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'linear-gradient(135deg, #4ade80, #22c55e)', color: '#fff', border: 'none' }}
                    >
                        ✓ Save & View Variant
                    </button>
                </div>
            </div>
        </div>
    );

    return null;
}

export default JDVariantModal;
